<template>
  <v-card flat class="pa-6">
    <h2 class="text-h5 primary-header sans-serif">
      {{ $t('form.import.results.title') }}
    </h2>
    <v-card-text>
      <v-row v-if="importResults && (createdCount > 0 || failedCount > 0)" justify="center">
        <v-col v-if="createdCount > 0" cols="12" md="6" lg="4">
          <v-card outlined class="text-center pa-4">
            <v-icon size="48" color="success">mdi-check-circle</v-icon>
            <div class="text-h4 mt-2">{{ createdCount }}</div>
            <div class="text-subtitle-1">{{ $t('form.import.results.successfullyImported') }}</div>
          </v-card>
        </v-col>
        <v-col v-if="failedCount > 0" cols="12" md="6" lg="4">
          <v-card outlined class="text-center pa-4">
            <v-icon size="48" color="warning">mdi-alert-circle</v-icon>
            <div class="text-h4 mt-2">{{ failedCount }}</div>
            <div class="text-subtitle-1">{{ $t('form.import.results.needAttention') }}</div>
          </v-card>
        </v-col>
      </v-row>

      <div v-if="importResults" class="mt-6">
        <v-tabs
          v-model="tab"
          color="primary"
          class="shrink"
        >
          <v-tab v-if="createdCount > 0">
            {{ $t('form.import.history.created') }} ({{ createdCount }})
          </v-tab>
          <v-tab v-if="failedCount > 0">
            {{ $t('form.import.history.failed') }} ({{ failedCount }})
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="tab" class="pt-3">
          <v-tab-item v-if="createdCount > 0">
            <div class="pa-4">
                <div v-if="createdCompanies.length > 0" class="mb-4">
                  <h4 class="text-subtitle-1 font-weight-bold mb-2">
                    {{ $t('form.import.history.companies') }} ({{ createdCompanies.length }})
                  </h4>
                  <v-simple-table dense>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th>{{ $t('form.import.results.headers.name') }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(company, index) in createdCompanies" :key="'c-' + index">
                          <td>{{ company.name || '-' }}</td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </div>

                <div v-if="createdIndividuals.length > 0" class="mb-4">
                  <h4 class="text-subtitle-1 font-weight-bold mb-2">
                    {{ $t('form.import.history.individuals') }} ({{ createdIndividuals.length }})
                  </h4>
                  <v-simple-table dense>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th>{{ $t('form.individual.preferredName') }}</th>
                          <th>{{ $t('form.individual.role') }}</th>
                          <th>{{ $t('form.individual.email') }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(individual, index) in createdIndividuals" :key="'i-' + index">
                          <td>{{ individual.preferredName || '-' }}</td>
                          <td>{{ individual.role ? $t(`shared.individual.roles.${individual.role}`) : '-' }}</td>
                          <td>{{ individual.email || '-' }}</td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </div>

                <div v-if="createdCommittees.length > 0">
                  <h4 class="text-subtitle-1 font-weight-bold mb-2">
                    {{ $t('form.import.history.committees') }} ({{ createdCommittees.length }})
                  </h4>
                  <v-simple-table dense>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th>{{ $t('form.import.results.headers.name') }}</th>
                          <th>{{ $t('form.import.history.members') }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(committee, index) in createdCommittees" :key="'cm-' + index">
                          <td>{{ committee.name || '-' }}</td>
                          <td>{{ getCommitteeMembers(committee) }}</td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </div>
            </div>
          </v-tab-item>

          <v-tab-item v-if="failedCount > 0">
            <div class="pa-4">
                <p class="text-body-2 mb-4">
                  {{ $t('form.import.results.itemsNeedingAttentionDescription') }}
                </p>

                <div v-if="failedCompanies.length > 0" class="mb-4">
                  <h4 class="text-subtitle-1 font-weight-bold mb-2">
                    {{ $t('form.import.history.companies') }} ({{ failedCompanies.length }})
                  </h4>
                  <p class="warning--text mb-2">
                    {{ $t('form.import.errors.importFailed') }}
                  </p>
                  <v-data-table
                    :headers="companyHeaders"
                    :items="failedCompanies"
                    :items-per-page="5"
                    dense
                  />
                </div>

                <div v-if="failedIndividuals.length > 0" class="mb-4">
                  <h4 class="text-subtitle-1 font-weight-bold mb-2">
                    {{ $t('form.import.history.individuals') }} ({{ failedIndividuals.length }})
                  </h4>
                  <v-data-table
                    :headers="individualHeaders"
                    :items="failedIndividuals"
                    :items-per-page="5"
                    dense
                  >
                    <template v-slot:[`item.role`]="{ item }">
                      {{ item.role ? $t(`shared.individual.roles.${item.role}`) : '-' }}
                    </template>
                    <template v-slot:[`item.actions`]="{ item }">
                      <v-btn small color="primary" @click="openManualImport('Individual', item)">
                        {{ $t('form.import.results.completeManually') }}
                      </v-btn>
                    </template>
                  </v-data-table>
                </div>

                <div v-if="failedCommittees.length > 0">
                  <h4 class="text-subtitle-1 font-weight-bold mb-2">
                    {{ $t('form.import.history.committees') }} ({{ failedCommittees.length }})
                  </h4>
                  <v-data-table
                    :headers="committeeHeaders"
                    :items="failedCommittees"
                    :items-per-page="5"
                    dense
                  >
                    <template v-slot:[`item.members`]="{ item }">
                      {{ formatFailedCommitteeMembers(item) }}
                    </template>
                    <template v-slot:[`item.actions`]="{ item }">
                      <v-btn small color="primary" @click="openManualImport('Committee', item)">
                        {{ $t('form.import.results.completeManually') }}
                      </v-btn>
                    </template>
                  </v-data-table>
                </div>
            </div>
          </v-tab-item>
        </v-tabs-items>
      </div>

      <v-row class="mt-6" justify="center">
        <v-col cols="auto">
          <platform-button primary @click="startNewImport">
            {{ $t('form.import.results.startNewImport') }}
          </platform-button>
        </v-col>
        <v-col cols="auto">
          <platform-button secondary @click="goToDashboard">
            {{ $t('form.import.results.goToDashboard') }}
          </platform-button>
        </v-col>
      </v-row>
    </v-card-text>

    <create-individual-modal
      v-if="showIndividualModal"
      :initial-data="selectedFailedItem"
      @close="closeModal"
      @created="handleManualCreated"
    />

    <create-committee-modal
      v-if="showCommitteeModal"
      :initial-data="committeeModalInitialData"
      import-completion-mode
      :import-member-names="committeeImportMemberNames"
      :match-candidates="committeeMatchCandidates"
      @close="closeModal"
      @created="handleManualCreated"
    />
  </v-card>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import API from '@/services/API'
import {
  BulkImportWizardFailedCommitteeRow,
  BulkImportWizardResponse,
  ImportEntityType
} from '@betterboards/shared/types/Import'
import { PlatformCompany } from '@betterboards/shared/types/Company'
import { PlatformIndividual } from '@betterboards/shared/types/Individual'
import { PlatformCommittee } from '@betterboards/shared/types/Committee'
import { CommitteeFormData } from '@betterboards/shared/helpers/import/transformers/committee'
import {
  transformToEntity,
  transformToFormData
} from '@betterboards/shared/helpers/import/transformers'
import CreateIndividualModal from '@/components/individuals/CreateIndividualModal.vue'
import CreateCommitteeModal from '@/components/committees/CreateCommitteeModal.vue'

type FailedItem = Partial<PlatformCompany> | Partial<PlatformIndividual> | BulkImportWizardFailedCommitteeRow
type CreatedEntity = PlatformCompany | PlatformIndividual | PlatformCommittee

export default Vue.extend({
  name: 'BulkImportResults',
  components: {
    CreateIndividualModal,
    CreateCommitteeModal
  },
  props: {
    importResults: {
      type: Object as PropType<BulkImportWizardResponse | null>,
      default: null
    },
    selectedCompanyId: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      tab: 0,
      showIndividualModal: false,
      showCommitteeModal: false,
      selectedFailedItem: null as FailedItem | null,
      selectedEntityType: null as ImportEntityType | null,
      committeeModalInitialData: null as CommitteeFormData | null,
      committeeImportMemberNames: [] as string[],
      committeeMatchCandidates: [] as PlatformIndividual[]
    }
  },
  computed: {
    createdCount (): number {
      if (!this.importResults) {
        return 0
      }
      const { created } = this.importResults
      return (created.company?.length ?? 0) + (created.individuals?.length ?? 0) + (created.committees?.length ?? 0)
    },
    failedCount (): number {
      if (!this.importResults) {
        return 0
      }
      const { failed } = this.importResults
      return (failed.company?.length ?? 0) + (failed.individuals?.length ?? 0) + (failed.committees?.length ?? 0)
    },
    createdCompanies (): PlatformCompany[] {
      return this.importResults?.created.company ?? []
    },
    createdIndividuals (): PlatformIndividual[] {
      return this.importResults?.created.individuals ?? []
    },
    createdCommittees (): PlatformCommittee[] {
      return this.importResults?.created.committees ?? []
    },
    failedCompanies (): Array<Partial<PlatformCompany>> {
      return this.importResults?.failed.company ?? []
    },
    failedIndividuals (): Array<Partial<PlatformIndividual>> {
      return this.importResults?.failed.individuals ?? []
    },
    failedCommittees (): BulkImportWizardFailedCommitteeRow[] {
      return this.importResults?.failed.committees ?? []
    },
    companyHeaders (): Array<{ text: string, value: string }> {
      return [
        { text: this.$t('form.import.results.headers.name') as string, value: 'name' }
      ]
    },
    individualHeaders (): Array<{ text: string, value: string }> {
      return [
        { text: this.$t('form.individual.preferredName') as string, value: 'preferredName' },
        { text: this.$t('form.individual.role') as string, value: 'role' },
        { text: this.$t('form.individual.email') as string, value: 'email' },
        { text: this.$t('form.import.results.headers.actions') as string, value: 'actions' }
      ]
    },
    committeeHeaders (): Array<{ text: string, value: string }> {
      return [
        { text: this.$t('form.import.results.headers.name') as string, value: 'name' },
        { text: this.$t('form.import.history.members') as string, value: 'members' },
        { text: this.$t('form.import.results.headers.actions') as string, value: 'actions' }
      ]
    }
  },
  methods: {
    async openManualImport (entityType: ImportEntityType, item: FailedItem): Promise<void> {
      this.selectedEntityType = entityType

      switch (entityType) {
        case 'Individual':
          this.selectedFailedItem = item
          this.showIndividualModal = true
          break
        case 'Committee': {
          const row = item as BulkImportWizardFailedCommitteeRow
          const entity = transformToEntity(row as Record<string, unknown>, 'Committee')
          const formData = transformToFormData(entity, 'Committee') as CommitteeFormData
          this.committeeModalInitialData = {
            name: formData.name,
            tags: formData.tags ?? [],
            individuals: formData.individuals ?? { items: [] }
          }
          this.committeeImportMemberNames = this.parseCommitteeMemberNames(row)
          this.committeeMatchCandidates = this.createdIndividuals
          try {
            await this.$store.dispatch('Individual/loadList', true)
          } catch (err) {
            console.error('Failed to refresh individuals before committee import completion:', err)
          }
          this.showCommitteeModal = true
          break
        }
        default:
          break
      }
    },
    parseCommitteeMemberNames (row: BulkImportWizardFailedCommitteeRow): string[] {
      if (!row.members) {
        return []
      }
      if (Array.isArray(row.members)) {
        return row.members.map(m => m.trim()).filter(m => m.length > 0)
      }
      return row.members.split(',').map(m => m.trim()).filter(m => m.length > 0)
    },
    formatFailedCommitteeMembers (committee: BulkImportWizardFailedCommitteeRow): string {
      if (!committee.members) {
        return '-'
      }
      return Array.isArray(committee.members)
        ? committee.members.join(', ')
        : committee.members
    },
    closeModal (): void {
      this.showIndividualModal = false
      this.showCommitteeModal = false
      this.selectedFailedItem = null
      this.selectedEntityType = null
      this.committeeModalInitialData = null
      this.committeeImportMemberNames = []
      this.committeeMatchCandidates = []
    },
    async handleManualCreated (createdEntity: CreatedEntity): Promise<void> {
      const companyId: string | undefined = this.selectedCompanyId
      const batchId: string | undefined = this.importResults?.batchId
      const entityType: ImportEntityType | null = this.selectedEntityType
      const entityId: string | undefined = createdEntity?.id

      if (!companyId || !batchId || !entityType || !entityId) {
        console.error('Cannot mark import batch manually completed: missing required fields', { companyId, batchId, entityType, entityId })
        this.closeModal()
        return
      }

      try {
        await API.patch('backendfunctions', '/company/import/batch', {
          body: {
            companyId,
            batchId,
            entityType,
            entityId
          }
        })
      } catch (err) {
        console.error('Failed to update import batch:', err)
      }

      this.closeModal()
    },
    startNewImport (): void {
      this.$router.go(0)
    },
    goToDashboard (): void {
      this.$router.push({ name: 'Home' })
    },
    getCommitteeMembers (committee: PlatformCommittee): string {
      const members = committee.individuals?.items
      if (!members || members.length === 0) {
        return '-'
      }
      return members
        .map(ci => {
          if (ci.individual?.preferredName) {
            return ci.individual.preferredName
          }
          const individual = this.createdIndividuals.find(i => i.id === ci.individualId)
          return individual?.preferredName
        })
        .filter(name => !!name)
        .join(', ') || '-'
    }
  }
})
</script>
